/**
 * The address check (relay protection, layer 1; SPEC.md §7.1). A relay
 * attacker streams our genuine live code onto a fake site, so the scanner
 * sees genuine, fresh, signed codes. Only the reader's address bar shows
 * which page they are on, so the scanner asks them to compare it with the
 * code's page. This module turns the code's page URL into what the reader
 * must compare: the registrable domain (via a small public-suffix list), the
 * full host, and warnings for look-alikes (IDN / punycode, mixed scripts).
 *
 * Runs in the scanner (bundled) and in the tests; no dependencies.
 */

// ---- a compact public-suffix list ---------------------------------------------------
// Not the whole PSL (≈ 9 000 rules, ~230 KB): the multi-label public suffixes
// people actually meet (country second levels and shared hosting platforms where
// anyone can get a subdomain). Any other host falls back to "one-label suffix",
// which is right for .dk, .com, .de, .io, …  A wildcard (`*.ck`) means every
// second-level name under it is a public suffix.
const MULTI = `
co.uk org.uk me.uk ltd.uk plc.uk net.uk sch.uk ac.uk gov.uk nhs.uk police.uk
com.au net.au org.au edu.au gov.au asn.au id.au
co.nz net.nz org.nz govt.nz ac.nz school.nz geek.nz
co.jp ne.jp or.jp ac.jp go.jp gr.jp ed.jp lg.jp
co.kr or.kr ne.kr go.kr ac.kr re.kr
com.cn net.cn org.cn gov.cn edu.cn ac.cn
com.hk net.hk org.hk gov.hk edu.hk idv.hk
com.tw net.tw org.tw gov.tw edu.tw idv.tw
com.sg net.sg org.sg gov.sg edu.sg per.sg
com.my net.my org.my gov.my edu.my
co.in net.in org.in firm.in gen.in ind.in gov.in ac.in edu.in
co.id or.id web.id ac.id go.id
com.ph net.ph org.ph gov.ph edu.ph
co.th in.th or.th ac.th go.th
com.vn net.vn org.vn gov.vn edu.vn
com.pk net.pk org.pk gov.pk edu.pk
co.il org.il net.il ac.il gov.il muni.il
com.tr net.tr org.tr gen.tr gov.tr edu.tr av.tr bel.tr biz.tr
com.br net.br org.br gov.br edu.br art.br blog.br eco.br
com.ar net.ar org.ar gob.ar gov.ar edu.ar int.ar
com.mx net.mx org.mx gob.mx edu.mx
com.co net.co org.co gov.co edu.co nom.co
com.pe net.pe org.pe gob.pe nom.pe
com.ve net.ve org.ve gob.ve
co.za org.za net.za gov.za ac.za web.za nom.za
com.ng org.ng gov.ng edu.ng
co.ke or.ke ne.ke go.ke ac.ke
com.eg org.eg gov.eg edu.eg
com.sa net.sa org.sa gov.sa edu.sa
com.ua net.ua org.ua gov.ua in.ua kiev.ua
com.ru net.ru org.ru msk.ru spb.ru
com.pl net.pl org.pl gov.pl edu.pl waw.pl
co.at or.at gv.at ac.at
com.es org.es nom.es gob.es edu.es
com.pt org.pt gov.pt edu.pt
com.gr org.gr gov.gr edu.gr net.gr
com.cy org.cy net.cy gov.cy
com.mt org.mt net.mt gov.mt edu.mt
co.hu org.hu
com.ro org.ro
co.no priv.no
co.it gov.it edu.it
com.de
eu.com uk.com us.com gb.net uk.net jpn.com
*.ck *.bd *.kh *.np *.er *.fk *.jm *.mm *.pg
github.io gitlab.io pages.dev workers.dev vercel.app netlify.app herokuapp.com
web.app firebaseapp.com appspot.com blogspot.com wordpress.com azurewebsites.net
cloudfront.net amazonaws.com s3.amazonaws.com elasticbeanstalk.com fly.dev onrender.com
glitch.me repl.co replit.dev ngrok.io ngrok-free.app trycloudflare.com
wixsite.com squarespace.com webflow.io carrd.co framer.website notion.site
myshopify.com azurestaticapps.net translate.goog
`.trim().split(/\s+/);
const SUFFIXES = new Set(MULTI.filter((s) => !s.startsWith('*.')));
const WILDCARDS = new Set(MULTI.filter((s) => s.startsWith('*.')).map((s) => s.slice(2)));

/** The public suffix of an ASCII host (lower case): the longest rule that matches, else the last label. */
export function publicSuffix(host: string): string {
  const labels = host.split('.');
  for (let i = 0; i < labels.length - 1; i++) {
    const rest = labels.slice(i).join('.');
    const parent = labels.slice(i + 1).join('.');
    if (SUFFIXES.has(rest)) return rest;
    if (WILDCARDS.has(parent)) return rest;
  }
  return labels[labels.length - 1];
}

/** The registrable domain ("eTLD+1"): y.dk for news.y.dk, bbc.co.uk for www.bbc.co.uk, evil.com for y.dk.evil.com. Null for a bare suffix. */
export function registrableDomain(host: string): string | null {
  const h = asciiHost(host);
  if (isIpHost(h)) return h;
  const suffix = publicSuffix(h);
  if (h === suffix) return null;
  const rest = h.slice(0, -(suffix.length + 1));
  return `${rest.slice(rest.lastIndexOf('.') + 1)}.${suffix}`;
}

const isIpHost = (h: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(h) || h.startsWith('[');

/** Lower-case ASCII (punycode) host; a Unicode host is converted the way a browser does (WHATWG URL). */
export function asciiHost(host: string): string {
  let h = host.trim().toLowerCase().replace(/\.$/, '');
  if (/[^\x00-\x7f]/.test(h)) h = new URL(`https://${h}/`).hostname;
  return h;
}

// ---- punycode (RFC 3492), decode only -------------------------------------------------
const BASE = 36, TMIN = 1, TMAX = 26, SKEW = 38, DAMP = 700;
function adapt(delta: number, n: number, first: boolean): number {
  delta = first ? Math.floor(delta / DAMP) : delta >> 1;
  delta += Math.floor(delta / n);
  let k = 0;
  for (; delta > ((BASE - TMIN) * TMAX) >> 1; k += BASE) delta = Math.floor(delta / (BASE - TMIN));
  return Math.floor(k + ((BASE - TMIN + 1) * delta) / (delta + SKEW));
}
/** Decodes one `xn--` label's payload (without the prefix). Throws on malformed input. */
export function punycodeDecode(input: string): string {
  const out: number[] = [];
  const b = input.lastIndexOf('-');
  for (let j = 0; j < Math.max(b, 0); j++) {
    if (input.charCodeAt(j) >= 0x80) throw new Error('not basic');
    out.push(input.charCodeAt(j));
  }
  let n = 128, i = 0, bias = 72;
  for (let idx = b > 0 ? b + 1 : 0; idx < input.length; ) {
    const oldi = i;
    for (let w = 1, k = BASE; ; k += BASE) {
      if (idx >= input.length) throw new Error('bad punycode');
      const c = input.charCodeAt(idx++);
      const digit = c >= 48 && c <= 57 ? c - 22 : c >= 65 && c <= 90 ? c - 65 : c >= 97 && c <= 122 ? c - 97 : BASE;
      if (digit >= BASE) throw new Error('bad punycode');
      i += digit * w;
      const t = k <= bias ? TMIN : k >= bias + TMAX ? TMAX : k - bias;
      if (digit < t) break;
      w *= BASE - t;
    }
    bias = adapt(i - oldi, out.length + 1, oldi === 0);
    n += Math.floor(i / (out.length + 1));
    i %= out.length + 1;
    out.splice(i++, 0, n);
  }
  return String.fromCodePoint(...out);
}

/** The host as Unicode (each `xn--` label decoded; a label that does not decode is kept as is). */
export function unicodeHost(ascii: string): string {
  return ascii
    .split('.')
    .map((l) => {
      if (!l.startsWith('xn--')) return l;
      try {
        return punycodeDecode(l.slice(4));
      } catch {
        return l;
      }
    })
    .join('.');
}

// ---- scripts ---------------------------------------------------------------------------
const SCRIPTS: Array<[string, RegExp]> = [
  ['Latin', /\p{Script=Latin}/u],
  ['Cyrillic', /\p{Script=Cyrillic}/u],
  ['Greek', /\p{Script=Greek}/u],
  ['Armenian', /\p{Script=Armenian}/u],
  ['Hebrew', /\p{Script=Hebrew}/u],
  ['Arabic', /\p{Script=Arabic}/u],
  ['Cherokee', /\p{Script=Cherokee}/u],
  ['Han', /\p{Script=Han}/u],
  ['Hiragana', /\p{Script=Hiragana}/u],
  ['Katakana', /\p{Script=Katakana}/u],
  ['Hangul', /\p{Script=Hangul}/u],
  ['Thai', /\p{Script=Thai}/u],
  ['Devanagari', /\p{Script=Devanagari}/u],
];
/** The writing systems used by a label's letters (digits, hyphens and marks do not count). */
export function scriptsOf(label: string): string[] {
  const found = new Set<string>();
  for (const ch of label) {
    if (!/\p{L}/u.test(ch)) continue;
    const s = SCRIPTS.find(([, re]) => re.test(ch));
    found.add(s ? s[0] : 'Other');
  }
  return [...found];
}
/** Japanese mixes Han + Hiragana + Katakana (+ Latin) by design; Chinese and Korean likewise with Han. Not a homograph sign. */
const CJK = new Set(['Han', 'Hiragana', 'Katakana', 'Hangul', 'Latin']);

export type AddressCheck = {
  /** The full page URL as the code names it. */
  url: string;
  /** Lower-case ASCII host, as a browser's address bar shows it for a non-IDN host. */
  host: string;
  /** What the reader must find in the address bar (ASCII / xn-- form). */
  registrable: string;
  /** The registrable domain in Unicode (= registrable when it is plain ASCII). */
  registrableUnicode: string;
  /** The labels before the registrable domain ("news" for news.y.dk), or "". */
  subdomain: string;
  /** Some label is an IDN (xn--): the address bar may show Unicode letters or the xn-- form. */
  idn: boolean;
  /** Different writing systems inside one label (e.g. Latin "y" + Cyrillic "а"): a classic look-alike. */
  mixedScript: boolean;
  scripts: string[];
  /** Plain-words warnings for the reader, most serious first. */
  warnings: string[];
};

/** Everything the address step shows, from the code's page URL. Throws when the URL has no usable host. */
export function addressCheck(pageUrl: string): AddressCheck {
  const u = new URL(pageUrl);
  const host = asciiHost(u.hostname);
  if (!host) throw new Error('the code names no page address');
  const registrable = registrableDomain(host) ?? host;
  const subdomain = host === registrable ? '' : host.slice(0, -(registrable.length + 1));
  const labels = host.split('.');
  const idn = labels.some((l) => l.startsWith('xn--'));
  const uni = unicodeHost(host);
  const registrableUnicode = unicodeHost(registrable);
  const perLabel = uni.split('.').map(scriptsOf);
  const scripts = [...new Set(perLabel.flat())];
  const mixedScript = perLabel.some((s) => s.length > 1 && !s.every((x) => CJK.has(x)));
  const nonLatin = scripts.some((s) => s !== 'Latin');
  const nonAscii = /[^\x00-\x7f]/.test(uni);
  const warnings: string[] = [];
  if (mixedScript) warnings.push(`This address mixes letters from different alphabets (${scripts.join(' + ')}). That is how look-alike addresses are made: treat it as a different website.`);
  if (idn || nonAscii)
    warnings.push(`This address contains ${nonLatin ? 'non-Latin' : 'accented or special'} letters (“${registrableUnicode}”). It is written ${registrable} in its plain form; your browser may show either.`);
  return { url: u.toString(), host, registrable, registrableUnicode, subdomain, idn, mixedScript, scripts, warnings };
}

/**
 * Does an address the reader typed (or the host of a URL) belong to the code's
 * registrable domain? news.y.dk and y.dk do; y.dk.evil.com and my-y.dk do not.
 * The scanner itself never reads the reader's address bar; this is for tests and
 * the "subdomains count" examples.
 */
export function sameSite(registrable: string, host: string): boolean {
  const h = asciiHost(host);
  return h === registrable || h.endsWith(`.${registrable}`);
}

/** Example look-alike for the hint line: `<registrable>.evil.com` style, never a real-looking address. */
export function subdomainExamples(registrable: string): { fine: string; notFine: string } {
  return { fine: `news.${registrable}`, notFine: `${registrable}.evil.com` };
}
